/**
 * Hook for tracking elapsed time from a start timestamp.
 *
 * Updates every second and survives page refreshes by using
 * server-provided timestamps.
 */

import { useEffect, useState } from 'react';

/**
 * Calculate elapsed time from a start timestamp.
 *
 * @param startTime - ISO 8601 timestamp string (e.g., "2026-01-27T18:00:00Z")
 * @returns Elapsed seconds since start time
 */
function calculateElapsed(startTime: string | null): number {
  if (!startTime) return 0;

  try {
    const start = new Date(startTime).getTime();
    const now = Date.now();
    return Math.floor((now - start) / 1000);
  } catch {
    // Invalid timestamp - return 0
    return 0;
  }
}

/**
 * Hook to track elapsed time from a start timestamp.
 *
 * Updates every second and uses server-provided timestamp
 * to survive page refreshes and provide consistent timing
 * across clients.
 *
 * @param startTime - ISO 8601 timestamp string or null
 * @returns Elapsed seconds (0 if no start time)
 *
 * @example
 * const elapsed = useElapsedTime(breakdown.breakdown_started_at);
 * const formatted = formatDuration(elapsed); // "2m 34s"
 */
export function useElapsedTime(startTime: string | null): number {
  const [elapsed, setElapsed] = useState(() => calculateElapsed(startTime));

  useEffect((): (() => void) | void => {
    if (!startTime) {
      setElapsed(0);
      return;
    }

    // Update immediately
    setElapsed(calculateElapsed(startTime));

    // Update every second
    const interval = setInterval(() => {
      setElapsed(calculateElapsed(startTime));
    }, 1000);

    return (): void => clearInterval(interval);
  }, [startTime]);

  return elapsed;
}
